import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ApiService } from '../services/apiService';
import { CardStateService } from '../services/cardStateService';
import { CardState } from '../types/cardTypes';
import EmailEntryLayout from '../components/EmailEntryLayout';
import { PageShell } from '../components/layout/PageShell';
import EmailValidation from '../components/EmailValidation';
// @mcc-dev-preview:import
import { parseDevState } from '../dev/devPreview';
// @mcc-dev-preview:import
import { DEV_CARD_ID, DEV_PREVIEW_USER } from '../dev/devPreviewMocks';
import './NewCustomer.css';

// @mcc-dev-preview:start dev-preview-types
type NewCustomerDevState =
  | 'loading'
  | 'email-form'
  | 'gift-card'
  | 'kyc-loading'
  | 'kyc-error'
  | 'welcome-back-modal';

const NEW_CUSTOMER_DEV_STATES: NewCustomerDevState[] = [
  'loading',
  'email-form',
  'gift-card',
  'kyc-loading',
  'kyc-error',
  'welcome-back-modal',
];
// @mcc-dev-preview:end dev-preview-types

const NewCustomer: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const devState = parseDevState(searchParams, NEW_CUSTOMER_DEV_STATES);
  const [cardId, setCardId] = useState<string | null>(null);
  const [currentEmail, setCurrentEmail] = useState<string>('');
  const [isCreatingVeriffSession, setIsCreatingVeriffSession] = useState(false);
  const [veriffError, setVeriffError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showAlreadyVerifiedModal, setShowAlreadyVerifiedModal] = useState(false);
  const [verifiedUserName, setVerifiedUserName] = useState<any>(null);
  const [isGiftCard, setIsGiftCard] = useState(false);

  const checkCardState = useCallback(async (cardId: string) => {
    try {
      const cardData = await ApiService.getCardByCardId(cardId);
      
      if (!cardData) {
        const errorMessage = `Card not found for ID: ${cardId}`;
        navigate(CardStateService.getErrorRoute(errorMessage, cardId));
        return;
      }

      if (cardData.state !== CardState.NEW && cardData.state !== CardState.GIFT_CARD) {
        const errorMessage = `Card is not in the expected state. Current state: ${cardData.state}`;
        navigate(CardStateService.getErrorRoute(errorMessage, cardId));
        return;
      }

      // Card is in correct state, continue
      setIsLoading(false);
    } catch (error: any) {
      console.error('Error checking card state:', error);
      const errorMessage = 'Failed to verify card state';
      navigate(CardStateService.getErrorRoute(errorMessage, cardId));
    }
  }, [navigate]);

  // @mcc-dev-preview:start dev-preview-seed
  useEffect(() => {
    if (!devState) return;

    setCardId(searchParams.get('cardId') || DEV_CARD_ID);

    if (devState === 'loading') {
      setIsLoading(true);
      return;
    }

    setIsLoading(false);

    switch (devState) {
      case 'gift-card':
        setIsGiftCard(true);
        break;
      case 'kyc-loading':
        setIsCreatingVeriffSession(true);
        break;
      case 'kyc-error':
        setVeriffError('Failed to create KYC session. Please try again.');
        break;
      case 'welcome-back-modal':
        setShowAlreadyVerifiedModal(true);
        setVerifiedUserName(DEV_PREVIEW_USER.firstName);
        break;
      case 'email-form':
      default:
        break;
    }
  }, [devState, searchParams]);
  // @mcc-dev-preview:end dev-preview-seed

  useEffect(() => {
    if (devState) return;

    const cardIdParam = searchParams.get('cardId');
    const giftCardParam = searchParams.get('giftcard');
    setCardId(cardIdParam);
    setIsGiftCard(giftCardParam === 'true');
    
    if (cardIdParam) {
      checkCardState(cardIdParam);
    } else {
      const errorMessage = 'Card ID is required';
      navigate(CardStateService.getErrorRoute(errorMessage));
    }
  }, [searchParams, navigate, checkCardState, devState]);

  const handleCloseAlreadyVerifiedModal = () => {
    setShowAlreadyVerifiedModal(false);
    setVerifiedUserName(null);
  };

  const handleProceedWithExistingAccount = async () => {
    if (!cardId) {
      return;
    }
    if (devState) return;

    try {
      const response = await ApiService.addCardToUser(cardId);
      
      setShowAlreadyVerifiedModal(false);
      navigate(`/card/${cardId}?fromNewCard=true`);

    } catch (error: any) {
      console.error('Error adding card to user:', error);
      // Handle error appropriately
    }
  };

  const handleCancelAlreadyVerifiedModal = () => {
    if (!cardId) {
      return;
    }
    if (devState) {
      setShowAlreadyVerifiedModal(false);
      setVerifiedUserName(null);
      return;
    }

    setShowAlreadyVerifiedModal(false);
    setVerifiedUserName(null);
    
    // Redirect to card page
    navigate(`/card/${cardId}`);
  };


  const handleCreateVeriffSession = async () => {
    if (!cardId) {
      setVeriffError('Card ID is required for KYC process');
      return;
    }
    if (devState) return;

    setIsCreatingVeriffSession(true);
    setVeriffError('');

    try {
      const result = await ApiService.createVeriffSession(cardId, currentEmail);
      
      if (result.success && result.data?.verification?.url) {
        // Redirect to Veriff KYC URL
        window.location.href = result.data.verification.url;
      } else {
        setVeriffError('Failed to create KYC session. Please try again.');
      }
    } catch (error: any) {
      console.error('Error creating Veriff session:', error);
      setVeriffError('Failed to create KYC session. Please try again.');
    } finally {
      setIsCreatingVeriffSession(false);
    }
  };

  const handleEmailValidationSuccess = async (email: string) => {
    if (devState) return;

    try {
      setCurrentEmail(email);
      
      // First, handle the user (create or get existing)
      await ApiService.handleEmailUser(email);
      
      // Then send verification email
      const response = await ApiService.sendVerificationEmail(email, cardId || '');

      if (response.success) {
        // Redirect to email-sent page
        navigate('/email-sent', { 
          state: { email: email, cardId: cardId || '' } 
        });
      } else {
        handleEmailValidationError('Failed to send verification email');
      }
    } catch (error: any) {
      console.error('Error sending verification email:', error);
      handleEmailValidationError('Network error. Please try again.');
    }
  };

  const handleEmailValidationError = (error: string) => {
    alert(`Error: ${error}`);
  };

  const giftCardBanner = isGiftCard ? (
    <div className="gift-card-header">
      <h2 className="gift-card-title">🎉 LUCKY YOU! 🎉</h2>
      <p className="gift-card-subtitle">
        Someone special has gifted you a My Crypto Card! 
        You're about to receive your very own crypto card loaded with digital currency.
      </p>
      <p className="gift-card-message">
        Enter your email address below to claim your gift and start your crypto journey!
      </p>
    </div>
  ) : null;

  if (isLoading) {
    return (
      <PageShell className="new-customer-figma">
        <div className="new-customer-loading-spinner">
          <div className="new-customer-spinner"></div>
          <p>Verifying card status...</p>
        </div>
      </PageShell>
    );
  }

  return (
    <>
      <EmailEntryLayout className="new-customer-figma" topContent={giftCardBanner}>
        <div className="email-form-container">
          <EmailValidation
            onValidationSuccess={handleEmailValidationSuccess}
            onValidationError={handleEmailValidationError}
            disabled={isLoading}
            className="new-customer-email-validation"
          />

          {isCreatingVeriffSession && (
            <div className="kyc-loading">
              <div className="kyc-spinner"></div>
              <p>Creating KYC session...</p>
              <p className="loading-subtitle">Please wait while we prepare your identity verification.</p>
            </div>
          )}

          {veriffError && (
            <div className="kyc-error">
              <p>{veriffError}</p>
              <button
                className="back-button"
                onClick={handleCreateVeriffSession}
                disabled={isCreatingVeriffSession}
              >
                {isCreatingVeriffSession ? 'Creating...' : 'Retry KYC'}
              </button>
            </div>
          )}
        </div>
      </EmailEntryLayout>

      {/* Already Verified Modal */}
      {showAlreadyVerifiedModal && (
        <div className="modal-overlay" onClick={handleCloseAlreadyVerifiedModal}>
          <div className="modal-content already-verified-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={handleCloseAlreadyVerifiedModal}>
              ×
            </button>
            
            <div className="modal-header">
              <h2 className="modal-title">Welcome back {verifiedUserName}!</h2>
              <p className="modal-subtitle">
                We've been able to locate your email in the system. 
                Thank you for returning. If you proceed, this card will be linked to the same email. If you choose to cancel, you will be redirected to start over.
              </p>
            </div>

            <div className="modal-body">
              <div className="already-verified-message">
                <p>Would you like to proceed?</p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="back-button"
                onClick={handleCancelAlreadyVerifiedModal}
              >
                Cancel
              </button>
              <button
                className="submit-button"
                onClick={handleProceedWithExistingAccount}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NewCustomer;
